import React from 'react';
import { MdOutlineGpsFixed, MdCalendarMonth, MdConfirmationNumber } from 'react-icons/md';
import {
  IoFlash, IoShieldCheckmark, IoConstruct, IoWifi, IoCellular, IoGlobe,
  IoTrash, IoServer, IoKey, IoBusiness, IoSpeedometer, IoPricetag,
  IoCarSport, IoCalendar, IoTime, IoPerson, IoInformationCircle,
  IoSettingsSharp, IoRocket, IoSwapHorizontal, IoExtensionPuzzle, IoBuild
} from 'react-icons/io5';
import { Row, Col } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import * as _ from 'lodash';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import Button from 'components/inputs/Button';

/* ──────────────────────────────────────────────
   Reusable info-row component
   ────────────────────────────────────────────── */
const InfoField = ({ label, value, icon }) => (
  <Col xl={3} md={6} className="mb-2">
    <div className="charger-info-field">
      {icon && <span className="charger-info-field__icon">{icon}</span>}
      <span className="charger-OEM">{label}:</span>{' '}
      <span className="charger-info-field__value">{value || '—'}</span>
    </div>
  </Col>
);

InfoField.propTypes = {
  label: PropTypes.string,
  value: PropTypes.any,
  icon: PropTypes.node,
};

/* ──────────────────────────────────────────────
   Section header helper
   ────────────────────────────────────────────── */
const SectionHeader = ({ icon, title }) => (
  <div className="charger-info-section-header">
    {icon && <span className="charger-info-section-header__icon">{icon}</span>}
    <span className="charger-info-section-header__title">{title}</span>
  </div>
);

SectionHeader.propTypes = { icon: PropTypes.node, title: PropTypes.string };

/* ──────────────────────────────────────────────
   Badge helper
   ────────────────────────────────────────────── */
const StatusBadge = ({ status }) => {
  if (!status) return <span>—</span>;
  const cls =
    status === 'Active' || status === 'Available'
      ? 'charger-badge charger-badge--active'
      : status === 'Expired' || status === 'Faulted' || status === 'Unavailable'
        ? 'charger-badge charger-badge--expired'
        : 'charger-badge charger-badge--na';
  return <span className={cls}>{status}</span>;
};

StatusBadge.propTypes = { status: PropTypes.string };

/* ──────────────────────────────────────────────
   Charge Speed Helper
   ────────────────────────────────────────────── */
const CHARGE_SPEED_MAP = {
  '6298f80101a9d276098db21c': 'Fast',
  '62a82c4ec68d55821deaf82a': 'Slow',
  '62a82c73c68d55159deaf84b': 'Mordrate',
  '62cffb37a0f82b1488726e32': 'Ultra Fast',
};

const getChargeSpeedLabel = (speed) => {
  if (!speed) return '—';
  const speedId = (typeof speed === 'object' ? speed.id || speed._id : speed);
  return CHARGE_SPEED_MAP[speedId] || _.get(speed, 'name', speed);
};

/* ══════════════════════════════════════════════
   Main component
   ══════════════════════════════════════════════ */
const ChargerInfo = () => {
  const chargerDetailData = useSelector((state) => state.charger.chargerDetails);
  const isLoader = useSelector((state) => state.charger.isLoading);
  const { t } = useTranslation();

  const fmt = (d) => (d ? moment(d).format('DD/MM/YYYY') : '');
  const connectionTypes = _.get(chargerDetailData, 'connectivity.connection_type', []);
  const activeTypes = Array.isArray(connectionTypes) ? connectionTypes : (connectionTypes ? [connectionTypes] : []);
  const connTypeLabel = activeTypes.join(', ') || '—';

  const getChargerPowerType = () => {
    const plugs = _.get(chargerDetailData, 'plugs', []);
    const oemPlugs = _.get(chargerDetailData, 'oem.plugs', []);
    if (_.isEmpty(plugs)) return '—';
    let foundDC = false;
    let foundAC = false;
    plugs.forEach((plug) => {
      const oemPlug = _.find(oemPlugs, (op) => String(op.number) === String(plug.connector_id)) || {};
      const mode = _.get(oemPlug, 'connector_type.connector_mode') || _.get(plug, 'external_connector_type.connector_mode');
      if (mode === 'DC') foundDC = true;
      if (mode === 'AC') foundAC = true;
    });
    if (foundDC) return 'DC';
    if (foundAC) return 'AC';
    return '—';
  };

  // ── Skeleton loader ──
  if (isLoader) {
    return (
      <SkeletonTheme baseColor="#1b2a38" highlightColor="#15212b" enableAnimation="true">
        <div className="charger-data-main">
          <Row className="charger-data-inner">
            {[...Array(4)].map((_, i) => (
              <Col xl={3} md={6} key={i}>
                <span className="charger-OEM"><Skeleton height={15} width={100} /></span>
              </Col>
            ))}
          </Row>
          <Row className="charger-data-inner">
            <Col xl={3} md={6}><span className="charger-OEM"><Skeleton height={15} width={100} /></span></Col>
          </Row>
        </div>
        <div className="charger-box-pages_main">
          <Row>
            <Col xl={6} md={12} className="charger-box-pages_inner">
              <div className="charger-box_block">
                <div className="charger-time charger-status--info">
                  <div className="charging-time_left" style={{ display: 'flex' }}>
                    <span className="charger-Tltc" style={{ display: 'flex' }}>
                      <span className="charger-watch_icon"><Skeleton height={15} width={15} circle="true" /></span>
                      <Skeleton height={15} width={100} />
                    </span>
                    <Skeleton height={15} width={100} />
                  </div>
                  <div className="charger-energy" style={{ display: 'flex' }}>
                    <span className="charger-ectn" style={{ display: 'flex' }}>
                      <span className="charger-enery_icon"><Skeleton height={15} width={15} circle="true" /></span>
                      <Skeleton height={15} width={100} />
                    </span>
                    <Skeleton height={15} width={100} />
                  </div>
                </div>
              </div>
            </Col>
            <Col xl={6} md={12} className="charger-data-page-main">
              <div className="charger-colom-inner ">
                <div className="charger-four">
                  <span className="charger-pluge"><Skeleton height={15} width={100} /></span>
                  <div className="charger-four-btn"><span><Skeleton height={15} width={100} /></span></div>
                </div>
                <Row>
                  <Col lg={6}><Skeleton height={15} width={15} /> <Skeleton height={15} width={100} /></Col>
                </Row>
              </div>
            </Col>
            <Col xl={6} md={12} className="charger-data-page-main">
              <div className="charger-comments_notes">
                <span className="charger-Comment"><Skeleton height={15} width={100} /></span>
                <p><Skeleton height={15} width={100} /></p>
              </div>
            </Col>
          </Row>
        </div>
      </SkeletonTheme>
    );
  }

  // ── Data view ──
  return (
    <React.Fragment>
      {/* ─── Basic Information ─── */}
      <div className="charger-data-main charger-info-section">
        <SectionHeader icon={<IoInformationCircle />} title="Basic Information" />
        <Row className="charger-data-inner">
          <InfoField label={t('chargerInfo.oem')} value={_.get(chargerDetailData, 'oem.vendor.name', '')} icon={<IoBusiness />} />
          <InfoField label={t('chargerInfo.chargerSpotModel')} value={_.get(chargerDetailData, 'oem.name', '')} icon={<IoSettingsSharp />} />
          <InfoField label={t('chargerInfo.rate')} value={`${_.get(chargerDetailData, 'tarrif.default_price_per_unit', '—')} / unit`} icon={<IoPricetag />} />
          <InfoField label={t('chargerInfo.chargingSpeed')} value={getChargeSpeedLabel(_.get(chargerDetailData, 'charging_speed'))} icon={<IoSpeedometer />} />
        </Row>
        <Row className="charger-data-inner">
          <InfoField label="Serial Number" value={_.get(chargerDetailData, 'serial_number')} icon={<MdConfirmationNumber />} />
          <InfoField label="Commissioning Date" value={fmt(_.get(chargerDetailData, 'installation_date'))} icon={<MdCalendarMonth />} />
          <InfoField label={t('addChargerForm.goLiveDate')} value={fmt(chargerDetailData.go_live_date)} icon={<IoRocket />} />
          <InfoField label={t('addChargerForm.ownershipType')} value={_.get(chargerDetailData, 'ownership_type', '')} icon={<IoSwapHorizontal />} />
        </Row>
        <Row className="charger-data-inner">
          <InfoField label={t('chargerInfo.parkingRestrictions')} value={_.get(chargerDetailData, 'parking_restrictions', false) ? 'Yes' : 'No'} icon={<IoCarSport />} />
          <InfoField label="Charger Power Type" value={getChargerPowerType()} icon={<IoFlash />} />
        </Row>
      </div>

      {/* ─── Plugs Information ─── */}
      <div className="charger-data-main charger-info-section">
        <SectionHeader icon={<IoExtensionPuzzle />} title={`Plugs Information (${_.size(_.get(chargerDetailData, 'plugs', []))})`} />
        <Row className="mt-3">
          {_.map(_.get(chargerDetailData, 'plugs', []), (plug, index) => {
            const status = _.get(plug, 'status', 'Unavailable');
            const statusClass = status.toLowerCase().replace(/\s+/g, '-');

            // Find corresponding plug definition from OEM
            const oemPlugs = _.get(chargerDetailData, 'oem.plugs', []);
            const oemPlug = _.find(oemPlugs, (op) => String(op.number) === String(plug.connector_id)) || {};
            const connectorType = _.get(oemPlug, 'connector_type') || _.get(plug, 'external_connector_type') || {};

            return (
              <Col xl={4} lg={4} md={6} key={`plug-info-${index}`} className="mb-3">
                <div className={`charger-plug-card charger-plug-card--status-${statusClass}`}>
                  <div className="charger-plug-card__header">
                    <span className="charger-plug-card__icon">
                      {_.get(connectorType, 'icon') ? (
                        <img src={_.get(connectorType, 'icon')} alt={_.get(connectorType, 'name')} className="plug-type-img" />
                      ) : (
                        <MdOutlineGpsFixed />
                      )}
                    </span>
                    <span className="charger-plug-card__title">{_.get(plug, 'name', `Plug ${index + 1}`)}</span>
                    <StatusBadge status={status} />
                  </div>
                  <div className="charger-plug-card__body">
                    <div className="charger-plug-item">
                      <IoExtensionPuzzle /> <span>ID: {_.get(plug, 'connector_id')}</span>
                    </div>
                    <div className="charger-plug-item">
                      <IoExtensionPuzzle /> <span>Type: {_.get(connectorType, 'name', '—')}</span>
                    </div>
                    <div className="charger-plug-item">
                      <IoSettingsSharp /> <span>Mode: {_.get(connectorType, 'connector_mode', '—')}</span>
                    </div>
                    <div className="charger-plug-item">
                      <IoGlobe /> <span>Standard: {_.get(connectorType, 'connector_standard', '—')}</span>
                    </div>
                    <div className="charger-plug-item">
                      <IoFlash /> <span>Max Voltage: {_.get(oemPlug, 'max_voltage') || _.get(plug, 'external_max_voltage') || 0} V</span>
                    </div>
                  </div>
                </div>
              </Col>
            );
          })}
        </Row>
      </div>

      {/* ─── Charger Power & System Info ─── */}
      <div className="charger-data-main charger-info-section">
        <SectionHeader icon={<IoFlash />} title="Charger Power & System Info" />
        <Row className="charger-data-inner">
          <InfoField label="Charging System Type" value={_.get(chargerDetailData, 'charging_system_type')} icon={<IoSettingsSharp />} />
          <InfoField label="OEM Charger Type" value={_.get(chargerDetailData, 'oem_charger_type')} icon={<IoBuild />} />
          <InfoField label="Dispenser Capacity" value={chargerDetailData.dispenser_capacity_kw ? `${chargerDetailData.dispenser_capacity_kw} kW` : null} icon={<IoFlash />} />
          <InfoField label="O/P Power Limit" value={chargerDetailData.output_power_limit_kw ? `${chargerDetailData.output_power_limit_kw} kW` : null} icon={<IoSpeedometer />} />
          <InfoField label="Onboarded On CMS By" value={_.get(chargerDetailData, 'onboarded_on_cms_by')} icon={<IoPerson />} />
        </Row>
      </div>

      {/* ─── Warranty Details ─── */}
      <div className="charger-data-main charger-info-section">
        <SectionHeader icon={<IoShieldCheckmark />} title="Warranty Details" />
        <Row className="charger-data-inner">
          <InfoField label="Period" value={chargerDetailData.warranty_period_years ? `${chargerDetailData.warranty_period_years} Year(s)` : null} icon={<IoTime />} />
          <InfoField label="Expired Date" value={fmt(chargerDetailData.warranty_expired_date)} icon={<IoCalendar />} />
          <Col xl={3} md={6} className="mb-2">
            <div className="charger-info-field">
              <span className="charger-info-field__icon"><IoShieldCheckmark /></span>
              <span className="charger-OEM">Status:</span>{' '}
              <StatusBadge status={_.get(chargerDetailData, 'warranty_status')} />
            </div>
          </Col>
        </Row>
      </div>

      {/* ─── AMC (Maintenance) ─── */}
      <div className="charger-data-main charger-info-section">
        <SectionHeader icon={<IoConstruct />} title="AMC (Maintenance)" />
        <Row className="charger-data-inner">
          <InfoField label="Start Date" value={fmt(chargerDetailData.amc_start_date)} icon={<IoCalendar />} />
          <InfoField label="Done Period" value={chargerDetailData.amc_done_period_years ? `${chargerDetailData.amc_done_period_years} Year(s)` : null} icon={<IoTime />} />
          <InfoField label="Expired Date" value={fmt(chargerDetailData.amc_expired_date)} icon={<IoCalendar />} />
          <Col xl={3} md={6} className="mb-2">
            <div className="charger-info-field">
              <span className="charger-info-field__icon"><IoConstruct /></span>
              <span className="charger-OEM">Status:</span>{' '}
              <StatusBadge status={_.get(chargerDetailData, 'amc_status')} />
            </div>
          </Col>
        </Row>
      </div>

      {/* ─── Connectivity Configuration ─── */}
      <div className="charger-data-main charger-info-section">
        <SectionHeader icon={<IoGlobe />} title="Connectivity Configuration" />
        <Row className="charger-data-inner">
          <InfoField label="Connection Type" value={connTypeLabel} icon={<IoGlobe />} />
        </Row>

        {/* SIM details */}
        {(activeTypes.includes('SIM') || activeTypes.includes('Dual SIM')) && (
          <div className="charger-info-subsection">
            {activeTypes.includes('SIM') && !activeTypes.includes('Dual SIM') && (
              <Row className="charger-data-inner">
                <InfoField label="Supports Dual SIM" value={_.get(chargerDetailData, 'connectivity.supports_dual_sim') ? 'Yes' : 'No'} icon={<IoCellular />} />
              </Row>
            )}
            {_.map(_.get(chargerDetailData, 'connectivity.sim', []), (sim, idx) => (
              <Row className="charger-data-inner" key={`sim-${idx}`}>
                <Col xl={12} className="mb-1">
                  <strong className="charger-info-sim-label">{sim.label || `SIM ${idx + 1}`}</strong>
                </Col>
                <InfoField label="Network Provider" value={sim.network_provider} icon={<IoCellular />} />
                <InfoField label="SIM Type" value={sim.sim_type} icon={<IoCellular />} />
                <InfoField label="SIM Number" value={sim.sim_number} icon={<MdConfirmationNumber />} />
              </Row>
            ))}
          </div>
        )}

        {/* WiFi */}
        {activeTypes.includes('WiFi') && (
          <Row className="charger-data-inner">
            <InfoField label="SSID" value={_.get(chargerDetailData, 'connectivity.wifi.ssid')} icon={<IoWifi />} />
            <InfoField label="Password" value={_.get(chargerDetailData, 'connectivity.wifi.password')} icon={<IoKey />} />
          </Row>
        )}

        {/* ILL / Broadband */}
        {activeTypes.includes('ILL') && (
          <Row className="charger-data-inner">
            <InfoField label="Network Provider" value={_.get(chargerDetailData, 'connectivity.broadband.network_provider')} icon={<IoServer />} />
            <InfoField label="SSID" value={_.get(chargerDetailData, 'connectivity.broadband.ssid')} icon={<IoWifi />} />
            <InfoField label="Password" value={_.get(chargerDetailData, 'connectivity.broadband.password')} icon={<IoKey />} />
          </Row>
        )}

        {/* LAN */}
        {activeTypes.includes('LAN') && (
          <Row className="charger-data-inner">
            <InfoField label="IP Configuration" value={_.get(chargerDetailData, 'connectivity.lan.ip_config')} icon={<IoServer />} />
          </Row>
        )}

        {/* VSAT */}
        {activeTypes.includes('VSAT') && (
          <Row className="charger-data-inner">
            <InfoField label="Provider" value={_.get(chargerDetailData, 'connectivity.vsat.provider')} icon={<IoServer />} />
            <InfoField label="Terminal ID" value={_.get(chargerDetailData, 'connectivity.vsat.terminal_id')} icon={<MdConfirmationNumber />} />
          </Row>
        )}
      </div>

      {/* ─── Lifecycle / Decommissioning ─── */}
      {_.get(chargerDetailData, 'operational_status') === 'Decommissioned' && (
        <div className="charger-data-main charger-info-section">
          <SectionHeader icon={<IoTrash />} title="Lifecycle / Decommissioning" />
          <Row className="charger-data-inner">
            <InfoField label="Decommissioned On" value={fmt(chargerDetailData.decommissioned_on)} icon={<IoCalendar />} />
            <InfoField label="Decommissioned By" value={_.get(chargerDetailData, 'decommissioned_by')} icon={<IoPerson />} />
          </Row>
        </div>
      )}

      {/* ─── Comments ─── */}
      <div className="charger-box-pages_main">
        <Row>
          <Col xl={6} md={12} className="charger-data-page-main">
            <div className="charger-comments_notes">
              <span className="charger-Comment">{t('chargerInfo.comments')}</span>
              <p>{_.get(chargerDetailData, 'comments', '')}</p>
            </div>
          </Col>
          <Col xl={6} md={12} className="charger-data-page-main" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
            <Button
              className="btn-primary"
              onClick={() => {
                const ocppId = _.get(chargerDetailData, 'charger_id', '');
                if (ocppId) {
                  window.open(`https://api-sitesync.chargecloud.net/v1/form/download/${ocppId}`, '_blank');
                }
              }}
            >
              View Installation & Commissioning Report
            </Button>
          </Col>
        </Row>
      </div>
    </React.Fragment>
  );
};
export default ChargerInfo;

export const PlugInfo = (props) => {
  const { icon, title } = props;
  return (
    <div className="charger-name_icon-data">
      <div className="charger-name--icon">
        <div className="charger-round_icon">{icon}</div>
        {title}
      </div>
    </div>
  );
};

PlugInfo.propTypes = {
  icon: PropTypes.node,
  title: PropTypes.number,
};
